/**
 * Adaptateur Worker /api/v1 — la seule source de données de l'app.
 * Enveloppe standard {data, meta} / {error} (docs/03-API.md).
 */
import type { ProTenant, CallLog, Opportunity, ServiceItem, AppNotification, LeadNote, DashboardStats } from '../../types';
import { loadStoredToken } from '../../features/auth/model/session.slice';

export const MASKED_PHONE = '06 45 ** ** 12';

/** Masque un numéro selon les droits : seuls tenant owner/agent voient le numéro complet. */
export function maskPhoneForRole(phone: string, canSeeFull: boolean): string {
  if (canSeeFull) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '****';
  return `${phone.slice(0, 5)} ** ** ${digits.slice(-2)}`;
}

export interface UsageEntry {
  id: string;
  callId?: string;
  type: string;
  quantity: number;
  occurredAt: string;
}

export interface DataApi {
  getTenant(tenantId?: string): Promise<ProTenant>;
  listProfessionals(): Promise<ProTenant[]>;
  updateTenant(tenant: ProTenant): Promise<ProTenant>;
  submitVerification(tenantId: string, documents?: string[]): Promise<{ requestId: string; status: string }>;
  listServices(): Promise<ServiceItem[]>;
  createService(service: Omit<ServiceItem, 'id'>): Promise<ServiceItem>;
  deleteService(serviceId: string): Promise<{ deleted: boolean }>;
  createPortfolioItem(item: { title: string; category?: string; imageUrl?: string; completionDate?: string; description?: string }): Promise<unknown>;
  deletePortfolioItem(itemId: string): Promise<{ deleted: boolean }>;
  listCalls(params?: { status?: string; limit?: number }): Promise<CallLog[]>;
  /** Journal client : appels du compte connecté, tous tenants confondus. */
  listMyCalls(limit?: number): Promise<CallLog[]>;
  listLeads(tenantId?: string): Promise<Opportunity[]>;
  updateLeadStage(leadId: string, stage: Opportunity['stage']): Promise<Opportunity>;
  listNotifications(): Promise<AppNotification[]>;
  markNotificationRead(notificationId: string): Promise<{ read: boolean }>;
  /** Inscription du jeton d'appareil (FCM/APNs) pour le push natif. */
  registerPushToken(payload: { token: string; platform: 'android' | 'ios' }): Promise<{ registered: boolean; platform: string }>;
  getLeadDetail(leadId: string): Promise<Opportunity & { notes: LeadNote[] }>;
  addLeadNote(leadId: string, note: string): Promise<LeadNote>;
  /** Agrégats temps réel du tableau de bord pro (`month` = YYYY-MM, sinon année en cours). */
  getDashboardStats(month?: string): Promise<DashboardStats>;
  /** Journal de consommation du tenant (GET /usage/history, plus récent d'abord). */
  getUsageHistory(limit?: number): Promise<UsageEntry[]>;
  /** URL signée temporaire (300 s) de l'enregistrement d'un appel. */
  getCallRecordingUrl(callId: string): Promise<{ url: string; expiresIn: number }>;
  /** Config du widget d'intégration (GET/PUT /pro/widget-config). */
  getWidgetConfig(): Promise<WidgetConfig>;
  updateWidgetConfig(config: WidgetConfig): Promise<WidgetConfig>;
}

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Config widget du tenant — contrat GET/PUT /pro/widget-config (Worker). */
export interface WidgetConfig {
  enabled: boolean;
  color: string;
  position: 'left' | 'right';
  ctaLabel: string;
  /** Origines du site du pro autorisées à appeler ; vide = toutes. */
  allowedOrigins: string[];
}

function isLikelyHtml(body: unknown): boolean {
  if (typeof body !== 'string') return false;
  const trimmed = body.trimStart();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html') || trimmed.startsWith('<head');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Le cookie de session n'est pas renvoyé en cross-site (pages.dev → workers.dev),
  // donc on joignait le jeton Bearer sinon chaque appel data 401.
  const token = loadStoredToken();
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Réponse non-JSON (ex. HTML du shell Vite, API mal configurée, 500 HTML de Cloudflare).
    const text = await res.text().catch(() => '');
    throw new Error(
      `Réponse API invalide (${res.status}) sur ${path} — la connexion ne renvoie pas du JSON. ` +
        `En local, utilisez VITE_API_URL=/api/v1 pour que Vite relaie vers Wrangler (localhost:8787), ` +
        `pas vers le shell Vite. Premier caractère reçu : ${text.slice(0, 80).replace(/\s+/g, ' ')}`,
    );
  }

  if (res.status === 401 || res.status === 403) {
    throw new Error(
      `Accès refusé (${res.status}) sur ${path}. ` +
        `Si la session a expiré, reconnectez-vous ; sinon vérifiez que le Worker API est joignable et que la session est valide.`,
    );
  }

  if (res.status >= 500) {
    throw new Error(
      `Erreur serveur (${res.status}) sur ${path}. ` +
        `Le backend n'a pas pu traiter la requête ; réessayez ou vérifiez les logs du Worker /api/v1.`,
    );
  }

  if (isLikelyHtml(body)) {
    throw new Error(
      `Réponse API inattendue (${res.status}) sur ${path} : JSON attendu, HTML reçu. ` +
        `Vérifiez que VITE_API_URL pointe vers /api/v1 (proxy Vite) ou vers le Worker API.`,
    );
  }

  if (!res.ok || (body && typeof body === 'object' && 'error' in body)) {
    const err = (body as Record<string, unknown>)?.error;
    const message = err && typeof err === 'object' && 'message' in err ? String((err as Record<string, unknown>).message) : `Erreur API (${res.status})`;
    throw new Error(message);
  }

  const typed = body as { data?: T; error?: unknown } | T | null | undefined;
  if (typed == null) {
    throw new Error(`Réponse API vide (${res.status}) sur ${path}.`);
  }

  return (body as { data?: T })?.data ?? (body as T);
}

/**
 * NOTE : /professionals renvoie le DTO complet (avec avis, services…) ;
 * pour la liste on allège côté Worker plus tard — le type UI reste stable.
 */
export const workerDataApi: DataApi = {
  getTenant: (tenantId) => request<ProTenant>(`/professionals/${tenantId}`),
  listProfessionals: () => request<ProTenant[]>('/professionals?pageSize=50'),
  updateTenant: (tenant) =>
    request(`/professionals/${tenant.id}`, { method: 'PATCH', body: JSON.stringify(tenant) }),
  submitVerification: (tenantId, documents = []) =>
    request(`/professionals/${tenantId}/submit-verification`, {
      method: 'POST',
      body: JSON.stringify({ documents }),
    }),
  listServices: () => request('/professional/services'),
  createService: (service) =>
    request('/professional/services', { method: 'POST', body: JSON.stringify(service) }),
  deleteService: (serviceId) =>
    request(`/professional/services/${serviceId}`, { method: 'DELETE' }),
  createPortfolioItem: (item) =>
    request('/professional/portfolio', { method: 'POST', body: JSON.stringify(item) }),
  deletePortfolioItem: (itemId) =>
    request(`/professional/portfolio/${itemId}`, { method: 'DELETE' }),
  listCalls: (params) =>
    request(`/calls?${new URLSearchParams({ ...(params?.status ? { status: params.status } : {}), ...(params?.limit ? { limit: String(params.limit) } : {}) })}`),
  listMyCalls: (limit = 50) => request(`/my-calls?limit=${limit}`),
  listLeads: (tenantId) => request(`/leads${tenantId ? `?tenantId=${tenantId}` : ''}`),
  updateLeadStage: (leadId, stage) =>
    request(`/leads/${leadId}/status`, { method: 'POST', body: JSON.stringify({ status: stage }) }),
  listNotifications: () => request('/notifications'),
  markNotificationRead: (notificationId) =>
    request(`/notifications/${notificationId}/read`, { method: 'POST' }),
  registerPushToken: (payload) =>
    request('/mobile/push-token', { method: 'POST', body: JSON.stringify(payload) }),
  getLeadDetail: (leadId) => request(`/leads/${leadId}`),
  addLeadNote: (leadId, note) =>
    request(`/leads/${leadId}/notes`, { method: 'POST', body: JSON.stringify({ note }) }),
  getDashboardStats: (month) => request(`/dashboard/stats${month ? `?month=${month}` : ''}`),
  getUsageHistory: (limit = 50) => request(`/usage/history?limit=${limit}`),
  getCallRecordingUrl: (callId) => request<{ url: string; expiresIn: number }>(`/calls/${callId}/recording-url`),
  getWidgetConfig: () => request('/pro/widget-config'),
  updateWidgetConfig: (config) =>
    request('/pro/widget-config', { method: 'PUT', body: JSON.stringify(config) }),
};

export const dataApi: DataApi = workerDataApi;

export type { ProTenant, CallLog, Opportunity, AppNotification };
